/**
 * Game registry — maps gameId strings to lazily-loaded React components.
 *
 * When a new game is added to the platform:
 *  1. Create its package under apps/ or packages/games/
 *  2. Add an entry here with a dynamic import
 *
 * The component receives the current gameState and a dispatch callback.
 */

import { lazy, type ComponentType } from 'react';
import type { Player, GameOptionDef } from '@playground/game-sdk';
import { apiFetch } from './api.js';

// ---------------------------------------------------------------------------
// Component contract
// ---------------------------------------------------------------------------

export interface GameComponentProps {
  gameState: unknown;
  players: Player[];
  localPlayerId: string;
  onAction: (action: unknown) => void;
  isMyTurn: boolean;
  /** Current credit balance of the local player. */
  credits?: number;
  /** Power-up action type → credit cost for this game. */
  powerUpCosts?: Record<string, number>;
  /** Max uses per match per action type (0 = unlimited). */
  powerUpUsesPerMatch?: Record<string, number>;
  /** How many times the local player has used each power-up this game session. */
  powerUpUsage?: Record<string, number>;
  /** Server-client clock offset in ms (positive = server ahead). Used for timer sync. */
  serverClockOffset?: number;
  /** Backend API base URL (platform API_BASE). Lets self-contained games (e.g.
   *  horse-racing) route their own backend calls to the game-server in deployed
   *  builds rather than the static SPA host. */
  apiBase?: string;
  /** Platform session token (the signed JWT this browser holds).
   *
   *  Only the self-contained games need it, and they need it for one thing: the
   *  `/api/loyalty/*` proxy injects the provider's secret key, so it refuses an
   *  unauthenticated call and pins every point movement to the player this token
   *  identifies. Without it a game's wallet is read-only — nothing burns and
   *  nothing pays. */
  authToken?: string | undefined;
  /** Social-casino profile picture URL for the local player, captured from the
   *  app launch flow. Self-contained games (horse/car racing) show it in their
   *  own top bar in place of the initials avatar. */
  avatarUrl?: string | undefined;
  /** Exit back to the platform (previous route). Provided for self-contained
   *  games (e.g. horse-racing) that render full-screen without the playground's
   *  own top-bar/back button, so the game can offer its own "back" affordance. */
  onExit?: () => void;
}

// ---------------------------------------------------------------------------
// Registry type
// ---------------------------------------------------------------------------

type LazyGameComponent = ReturnType<typeof lazy<ComponentType<GameComponentProps>>>;

interface GameMeta {
  id: string;
  name: string;
  description: string;
  minPlayers: number;
  maxPlayers: number;
  estimatedDurationMinutes: number;
  tags: string[];
  component: LazyGameComponent;
  /** Bullet-point rules shown in the in-room rules modal. */
  howToPlay: string[];
  /** True for games whose words can be AI-generated (language + difficulty selectors shown at creation). */
  isWordGame?: boolean;
  /** Host-configurable options rendered in the game details panel before starting. */
  options?: readonly GameOptionDef[];
  /** If true, a fullscreen toggle button is shown during gameplay. */
  supportsFullscreen?: boolean;
}

// ---------------------------------------------------------------------------
// Registered games
// ---------------------------------------------------------------------------

/**
 * Add entries here for each game.  The dynamic import is resolved at runtime
 * so each game bundle is code-split automatically.
 *
 * Example (uncomment when the TicTacToe game package exists):
 *
 * {
 *   id: 'tictactoe',
 *   name: 'Tic-Tac-Toe',
 *   description: 'Classic 3-in-a-row strategy game.',
 *   minPlayers: 2,
 *   maxPlayers: 2,
 *   estimatedDurationMinutes: 5,
 *   tags: ['classic', 'strategy'],
 *   component: lazy(() => import('@playground/game-tictactoe/component')),
 * },
 */
const GAMES: GameMeta[] = [
  {
    id: 'tictactoe',
    name: 'Tic-Tac-Toe',
    description: 'Classic 3-in-a-row strategy game for 2 players.',
    minPlayers: 2,
    maxPlayers: 2,
    estimatedDurationMinutes: 5,
    tags: ['classic', 'strategy'],
    howToPlay: [
      'Players take turns placing their mark (X or O) on a 3×3 grid.',
      'The first player to get 3 marks in a row (horizontally, vertically, or diagonally) wins.',
      'If all 9 squares are filled with no winner, the game is a draw.',
    ],
    component: lazy(() =>
      import('@playground/game-tictactoe/component').then((m) => ({ default: m.Board })),
    ),
  },
  {
    id: 'hangman',
    name: 'Hangman',
    description: 'Guess the hidden word letter by letter before the lives run out. 2–10 players!',
    minPlayers: 2,
    maxPlayers: 10,
    estimatedDurationMinutes: 10,
    tags: ['word', 'classic', 'family'],
    isWordGame: true,
    howToPlay: [
      'A secret word is hidden — you see only dashes representing each letter.',
      'Players take turns guessing one letter at a time.',
      'A correct guess reveals the letter in its position(s) and scores points equal to its frequency.',
      'A wrong guess adds a body part to the hangman. You have 8 lives.',
      'Win by completing the word before the hangman is fully drawn. Lose if lives run out.',
    ],
    component: lazy(() =>
      import('@playground/game-hangman/component').then((m) => ({ default: m.Board })),
    ),
  },
  {
    id: 'bluff-quiz',
    name: 'Bluff Quiz',
    description: 'Write convincing fake answers and fool your friends. Social trivia at its best!',
    minPlayers: 2,
    maxPlayers: 8,
    estimatedDurationMinutes: 15,
    tags: ['family', 'trivia', 'classic'],
    isWordGame: true,
    howToPlay: [
      'Each round a trivia question is shown. One answer is real — all others are invented by players.',
      'Everyone writes a fake answer that sounds convincing.',
      'All answers (real + fake) are shuffled and displayed for a vote.',
      'Score points for voting for the real answer, and for every player who votes for YOUR fake.',
      'Most points after all rounds wins!',
    ],
    component: lazy(() =>
      import('@playground/game-bluff-quiz/component').then((m) => ({ default: m.Board })),
    ),
  },
  {
    id: 'reaction-battle',
    name: 'Reaction Battle',
    description: 'Pure reflexes — tap the button first when GO appears. Watch out for fakes!',
    minPlayers: 2,
    maxPlayers: 8,
    estimatedDurationMinutes: 5,
    tags: ['family', 'kids'],
    howToPlay: [
      'Each round, the host arms the round and a random delay begins.',
      'When the signal fires, two buttons appear: TAP! (green) and FAKE! (red).',
      'Tap TAP! as fast as possible if the signal is real — fastest player scores most points.',
      'If you think the signal is fake, press FAKE! instead for a +300 pt bonus.',
      'Tapping on a fake costs you −500 pts. Pressing FAKE! on a real signal scores 0.',
    ],
    component: lazy(() =>
      import('@playground/game-reaction-battle/component').then((m) => ({ default: m.Board })),
    ),
  },
  {
    id: 'draw-guess',
    name: 'Draw & Guess',
    description: 'One player draws, everyone else guesses. Laughs guaranteed!',
    supportsFullscreen: true,
    minPlayers: 2,
    maxPlayers: 8,
    estimatedDurationMinutes: 20,
    tags: ['creative', 'family'],
    isWordGame: true,
    howToPlay: [
      'Each round, the drawer is shown 3 words (easy / medium / hard) and picks one to draw.',
      'The other players type guesses into the chat. You have 70 seconds.',
      'Correct guessers score points — earlier guesses score more.',
      'The drawer earns bonus points for every player who guesses correctly.',
      'Roles rotate each round so everyone gets to draw.',
    ],
    component: lazy(() =>
      import('@playground/game-draw-guess/component').then((m) => ({ default: m.Board })),
    ),
  },
  {
    id: 'trivia-blitz',
    name: 'Trivia Blitz',
    description: 'Answer trivia questions simultaneously — faster correct answers score more points!',
    supportsFullscreen: true,
    minPlayers: 2,
    maxPlayers: 10,
    estimatedDurationMinutes: 10,
    tags: ['trivia', 'family', 'kids'],
    isWordGame: true,
    howToPlay: [
      'All players see the same trivia question at the same time.',
      'Pick the correct answer (A, B, C, or D) as fast as you can.',
      'Correct answers score up to 1000 points — the faster you answer, the more you get!',
      'Wrong or unanswered questions score 0 points.',
      'After all rounds, the player with the highest total score wins.',
    ],
    component: lazy(() =>
      import('@playground/game-trivia-blitz/component').then((m) => ({ default: m.Board })),
    ),
  },
  {
    id: 'rock-paper-scissors',
    name: 'Rock Paper Scissors',
    description: 'The classic hand game — now multiplayer! Everyone picks simultaneously.',
    minPlayers: 2,
    maxPlayers: 8,
    estimatedDurationMinutes: 5,
    tags: ['classic', 'family', 'kids'],
    howToPlay: [
      'Each round all players simultaneously choose Rock 🪨, Paper 📄, or Scissors ✂️.',
      'Rock beats Scissors, Scissors beats Paper, Paper beats Rock.',
      'You score points against every other player you beat (2 pts per win, 1 pt per draw).',
      'After 5 rounds the player with the most total points wins.',
    ],
    component: lazy(() =>
      import('@playground/game-rock-paper-scissors/component').then((m) => ({ default: m.Board })),
    ),
  },
  {
    id: 'math-blitz',
    name: 'Math Blitz',
    description: 'Race the clock — solve math operations faster than everyone else!',
    supportsFullscreen: true,
    minPlayers: 1,
    maxPlayers: 10,
    estimatedDurationMinutes: 3,
    tags: ['family', 'kids'],
    howToPlay: [
      'Each player gets their own independent math question.',
      'Solve it and type the answer — faster correct answers score more points!',
      'Get 3 correct in a row to level up to harder operations.',
      'Wrong answers cost you points, so think before you submit.',
      'The game ends when the timer runs out. Most points wins!',
    ],
    options: [
      {
        key: 'timerMinutes',
        type: 'select' as const,
        label: 'Match duration',
        choices: [
          { value: 1, label: '1 min' },
          { value: 2, label: '2 min' },
          { value: 3, label: '3 min' },
          { value: 5, label: '5 min' },
        ],
        default: 3,
      },
    ],
    component: lazy(() =>
      import('@playground/game-math-blitz/component').then((m) => ({ default: m.Board })),
    ),
  },
  {
    id: 'bingo',
    name: 'Bingo',
    description: 'Mark numbers on your card as the host draws them. First to complete a line wins!',
    minPlayers: 2,
    maxPlayers: 20,
    estimatedDurationMinutes: 15,
    tags: ['classic', 'family', 'kids'],
    howToPlay: [
      'Each player gets a unique 5×5 card with random numbers and a FREE center space.',
      'The host draws numbers one by one — mark them on your card.',
      'Complete a full row, column, or diagonal to claim Bingo!',
      'Click "Claim Bingo!" before anyone else to win the round.',
    ],
    component: lazy(() =>
      import('@playground/game-bingo/component').then((m) => ({ default: m.Board })),
    ),
  },
  {
    id: 'higher-or-lower',
    name: 'Higher or Lower',
    description: 'Guess whether the next number will be higher or lower than the current one!',
    minPlayers: 2,
    maxPlayers: 10,
    estimatedDurationMinutes: 5,
    tags: ['family', 'kids', 'classic'],
    howToPlay: [
      'A number between 1 and 100 is shown on screen.',
      'All players simultaneously guess whether the next number will be Higher or Lower.',
      'Correct guesses score a point — ties mean everyone scores.',
      'Most points after all rounds wins!',
    ],
    options: [
      {
        key: 'totalRounds',
        type: 'select' as const,
        label: 'Rounds',
        choices: [
          { value: 5, label: '5' },
          { value: 10, label: '10' },
          { value: 15, label: '15' },
        ],
        default: 10,
      },
    ],
    component: lazy(() =>
      import('@playground/game-higher-or-lower/component').then((m) => ({ default: m.Board })),
    ),
  },
  {
    id: 'two-truths',
    name: 'Two Truths and a Lie',
    description: 'Write 2 true statements and 1 lie. Can your friends spot the lie?',
    minPlayers: 3,
    maxPlayers: 10,
    estimatedDurationMinutes: 20,
    tags: ['creative', 'family'],
    howToPlay: [
      'Each round, one player writes 3 statements about themselves — 2 true, 1 lie.',
      'The statements are shuffled and shown to everyone else.',
      'Other players vote on which statement they think is the lie.',
      'Correct voters score a point. The active player scores 1 point for every person they fool.',
    ],
    component: lazy(() =>
      import('@playground/game-two-truths/component').then((m) => ({ default: m.Board })),
    ),
  },
  {
    id: 'wordle-race',
    name: 'Wordle Race',
    description: 'Race to guess the secret 5-letter word before other players! Fewer guesses = more points.',
    minPlayers: 2,
    maxPlayers: 10,
    estimatedDurationMinutes: 10,
    tags: ['word', 'family'],
    isWordGame: true,
    howToPlay: [
      'All players try to guess the same secret 5-letter word.',
      'After each guess, tiles show: Green = correct position, Yellow = wrong position, Gray = not in word.',
      'You have up to 6 guesses. Solve in fewer guesses for more points!',
      'The first player to solve it gets a bonus. Most points overall wins.',
    ],
    component: lazy(() =>
      import('@playground/game-wordle-race/component').then((m) => ({ default: m.Board })),
    ),
  },
  {
    id: 'word-chain',
    name: 'Word Chain',
    description: "Say a word that starts with the last letter of the previous word. Get 3 strikes and you're out!",
    minPlayers: 2,
    maxPlayers: 12,
    estimatedDurationMinutes: 10,
    tags: ['word', 'family'],
    howToPlay: [
      'Players take turns saying a word that starts with the last letter of the previous word.',
      'Words must be at least 3 letters, no repeating words allowed.',
      'You have 30 seconds per turn — too slow or an invalid word earns a strike.',
      '3 strikes and you are eliminated. Last player standing wins!',
    ],
    component: lazy(() =>
      import('@playground/game-word-chain/component').then((m) => ({ default: m.Board })),
    ),
  },
  {
    id: 'categories',
    name: 'Categories',
    description: 'Name as many things as you can for each category. Only UNIQUE answers score points!',
    minPlayers: 2,
    maxPlayers: 12,
    estimatedDurationMinutes: 15,
    tags: ['word', 'family', 'trivia'],
    isWordGame: true,
    howToPlay: [
      'Each round reveals a category (e.g. "Animals that can swim").',
      'All players simultaneously write as many answers as they can.',
      'Only UNIQUE answers score — if two players write the same thing, neither gets the point.',
      'Most total points after all rounds wins!',
    ],
    options: [
      {
        key: 'totalRounds',
        type: 'select' as const,
        label: 'Number of Rounds',
        choices: [
          { value: 3, label: '3 rounds' },
          { value: 6, label: '6 rounds' },
          { value: 9, label: '9 rounds' },
        ],
        default: 6,
      },
    ],
    component: lazy(() =>
      import('@playground/game-categories/component').then((m) => ({ default: m.Board })),
    ),
  },
  {
    id: 'speed-typing',
    name: 'Speed Typing',
    description: 'Race to type phrases as fast and accurately as possible. Fastest accurate typist wins!',
    minPlayers: 2,
    maxPlayers: 8,
    estimatedDurationMinutes: 10,
    tags: ['word', 'family'],
    isWordGame: true,
    howToPlay: [
      'Each round shows the same phrase to all players.',
      'Type it as fast and accurately as you can — only 100% correct submissions count!',
      'Fastest correct submission scores the most points.',
      'Most points after all rounds wins!',
    ],
    component: lazy(() =>
      import('@playground/game-speed-typing/component').then((m) => ({ default: m.Board })),
    ),
  },
  {
    id: 'connect-four',
    name: 'Connect Four',
    description: 'Drop colored discs to connect four in a row — horizontally, vertically, or diagonally!',
    minPlayers: 2,
    maxPlayers: 2,
    estimatedDurationMinutes: 10,
    tags: ['classic', 'strategy'],
    howToPlay: [
      'Players alternate dropping their colored discs into a 7-column, 6-row grid.',
      'Discs fall to the lowest available row in the chosen column.',
      'First player to connect 4 of their discs in a row (horizontal, vertical, or diagonal) wins!',
      'If the grid fills up with no winner, it is a draw.',
    ],
    options: [
      {
        key: 'totalGames',
        type: 'select' as const,
        label: 'Best of',
        choices: [
          { value: 1, label: '1 game' },
          { value: 3, label: 'Best of 3' },
          { value: 5, label: 'Best of 5' },
        ],
        default: 1,
      },
    ],
    component: lazy(() =>
      import('@playground/game-connect-four/component').then((m) => ({ default: m.Board })),
    ),
  },
  {
    id: 'battleship',
    name: 'Battleship',
    description: 'Sink all enemy ships before they sink yours. Classic naval combat for 2 players!',
    minPlayers: 2,
    maxPlayers: 2,
    estimatedDurationMinutes: 15,
    tags: ['classic', 'strategy'],
    howToPlay: [
      'Each player has a 10×10 grid with 5 ships placed randomly.',
      'Take turns calling out grid coordinates to attack your opponent.',
      "Hit = 💥, Miss = ○. A ship is sunk when all its cells are hit.",
      'Sink all 5 enemy ships first to win!',
    ],
    component: lazy(() =>
      import('@playground/game-battleship/component').then((m) => ({ default: m.Board })),
    ),
  },
  {
    id: 'checkers',
    name: 'Checkers',
    description: "Classic draughts on an 8×8 board. Jump your opponent's pieces and king your men!",
    minPlayers: 2,
    maxPlayers: 2,
    estimatedDurationMinutes: 20,
    tags: ['strategy', 'classic', 'family'],
    howToPlay: [
      'Red pieces start at the bottom, black at the top. Move diagonally forward.',
      'Jump over an opponent piece to capture it — captures are mandatory when available.',
      'Reach the far end to become a King — kings can move and capture in any diagonal direction.',
      'Win by capturing all opponent pieces or leaving them with no legal moves.',
    ],
    component: lazy(() =>
      import('@playground/game-checkers/component').then((m) => ({ default: m.Board })),
    ),
  },
  {
    id: 'mafia',
    name: 'Mafia',
    description: 'Social deduction: Mafia vs. Villagers. Survive the night and root out the bad guys!',
    minPlayers: 4,
    maxPlayers: 15,
    estimatedDurationMinutes: 25,
    tags: ['family', 'strategy', 'classic'],
    howToPlay: [
      'Players are secretly assigned roles: Mafia, Detective, Doctor, or Villager.',
      'At night: Mafia choose someone to eliminate, Doctor saves one person, Detective investigates one person.',
      'During the day: discuss who you think is Mafia, then vote to eliminate someone.',
      'Villagers win when all Mafia are eliminated. Mafia wins when they match or outnumber villagers.',
    ],
    component: lazy(() =>
      import('@playground/game-mafia/component').then((m) => ({ default: m.Board })),
    ),
  },
  {
    id: 'emoji-story',
    name: 'Emoji Story',
    description: 'Tell a story using 5 random emojis. The best storyteller wins the crowd!',
    minPlayers: 3,
    maxPlayers: 8,
    estimatedDurationMinutes: 15,
    tags: ['creative', 'family', 'word'],
    howToPlay: [
      'Each round, 5 random emojis are revealed to all players.',
      'Write a short story (up to 200 characters) that incorporates all 5 emojis.',
      'Stories are shown anonymously — vote for your favorite (not your own).',
      'Most votes wins the round. Most round wins overall wins the game!',
    ],
    options: [
      {
        key: 'rounds',
        type: 'select' as const,
        label: 'Rounds',
        choices: [
          { value: 2, label: '2 rounds' },
          { value: 3, label: '3 rounds' },
          { value: 5, label: '5 rounds' },
        ],
        default: 3,
      },
    ],
    component: lazy(() =>
      import('@playground/game-emoji-story/component').then((m) => ({ default: m.Board })),
    ),
  },
  {
    id: 'cards-racing',
    name: 'Cards Racing',
    description: 'Bet on which ace crosses the finish line first as cards are drawn from a 6-deck shoe!',
    minPlayers: 1,
    maxPlayers: 100,
    estimatedDurationMinutes: 5,
    tags: ['family', 'classic', 'solo'],
    howToPlay: [
      'Everyone plays at the same global table — no room needed!',
      '4 aces (♠♥♦♣) race to position 4. Cards are drawn from a 6-deck shoe.',
      'During the betting window, place pre-game bets: suit winner, colour, first card, exacta, trifecta, and more.',
      'Live bets with dynamic odds (updated via Monte Carlo simulation) are available during the race.',
      'When all 4 aces reach the same position, a hidden penalty card is revealed — that suit retreats one step!',
      'First ace to reach position 4 wins. Payouts are applied automatically.',
      'Key payouts: Suit winner 4.00x · Color winner 2.00x · Exacta 12.00x · Trifecta 24.00x',
      'Rarer bets: Photo finish 8.29x · Margin = 4: 67.68x · Winner = first retreat: 14.42x',
      'Tap ? inside the game for the full odds table with current house edge applied.',
    ],
    component: lazy(() =>
      import('@playground/game-cards-racing/component').then((m) => ({ default: m.Board })),
    ),
  },
  {
    id: 'horse-racing',
    name: 'Horse Racing',
    description: 'Bet on the horses, then watch them thunder to the finish line!',
    minPlayers: 1,
    maxPlayers: 100,
    estimatedDurationMinutes: 5,
    tags: ['family', 'classic', 'solo'],
    howToPlay: [
      'Everyone plays at the same global table — no room needed!',
      'During the 30-second betting window, pick your bet:',
      'Win — your horse finishes 1st. Place — your horse finishes in the top 2.',
      'Exacta — pick 1st and 2nd in exact order. Trifecta — pick the top 3 in exact order.',
      'Odds are computed from a Monte Carlo simulation of the race physics and lock in at bet time.',
      'When betting closes the gates open — first horse past the post wins. Payouts are applied automatically.',
      'The race seed is committed (hashed) before betting and revealed when the race ends — provably fair.',
    ],
    component: lazy(() =>
      import('@playground/game-horse-racing/component').then((m) => ({ default: m.Board })),
    ),
  },
  {
    id: 'car-racing',
    name: 'Car Racing',
    description: 'Bet on the supercars, then watch them roar to the finish line!',
    minPlayers: 1,
    maxPlayers: 100,
    estimatedDurationMinutes: 5,
    tags: ['family', 'classic', 'solo'],
    howToPlay: [
      'Everyone plays at the same global table — no room needed!',
      'During the 30-second betting window, pick your bet:',
      'Win — your car finishes 1st. Place — your car finishes in the top 2.',
      'Exacta — pick 1st and 2nd in exact order. Trifecta — pick the top 3 in exact order.',
      'Odds are computed from a Monte Carlo simulation of the race physics and lock in at bet time.',
      'When betting closes the lights go green — first car across the line wins. Payouts are applied automatically.',
      'The race seed is committed (hashed) before betting and revealed when the race ends — provably fair.',
    ],
    component: lazy(() =>
      import('@playground/game-car-racing/component').then((m) => ({ default: m.Board })),
    ),
  },
  {
    id: 'bubble-rush',
    name: 'Bubble Rush',
    description: 'Bet credits, watch the bubble grow, and cash out before it pops!',
    minPlayers: 1,
    maxPlayers: 50,
    estimatedDurationMinutes: 10,
    tags: ['family', 'classic', 'solo'],
    howToPlay: [
      'Everyone plays at the same global table — no room needed!',
      'Each round, choose how many credits to bet (10 · 25 · 50 · 100 · 250 · 500).',
      'The bubble starts inflating and your multiplier grows from 1.00x upward.',
      'Cash out before the bubble pops to win your bet × the multiplier.',
      'If the bubble pops before you cash out, you lose your bet.',
      'The crash point is random and hidden — it can pop at any moment!',
    ],
    component: lazy(() =>
      import('@playground/game-bubble-rush/component').then((m) => ({ default: m.Board })),
    ),
  },
  {
    id: 'moka-crash',
    name: 'Moka Rush',
    description: 'Pressure builds, the multiplier rises — cash out before the moka explodes!',
    minPlayers: 1,
    maxPlayers: 50,
    estimatedDurationMinutes: 10,
    tags: ['family', 'classic', 'solo'],
    howToPlay: [
      'Everyone plays at the same global table — no room needed!',
      'Each round, choose how many credits to bet (10 · 25 · 50 · 100 · 250 · 500).',
      'The moka starts heating up and your multiplier grows from 1.00x upward.',
      'Cash out before the moka explodes to win your bet × the multiplier.',
      'If the moka explodes before you cash out, you lose your bet.',
      'The crash point is random and hidden — it can happen at any moment!',
    ],
    component: lazy(() =>
      import('@playground/game-moka-crash/component').then((m) => ({ default: m.Board })),
    ),
  },
  {
    id: 'aether-dynasty',
    name: 'Aether Dynasty',
    description: 'A 46,656-ways neon-antiquity slot — cascading wins, wilds, golden frames and free games.',
    minPlayers: 1,
    maxPlayers: 1000,
    estimatedDurationMinutes: 5,
    tags: ['solo', 'classic', 'family'],
    howToPlay: [
      '6 reels, "ways" pays — wins pay left→right from reel 1 on adjacent reels, 3+ symbols to win.',
      'Winning symbols cascade away, new symbols drop in, and wins keep resolving until none remain.',
      'Each winning cascade expands the board, growing the ways toward the maximum 46,656.',
      'Golden Frames (reels 2–5) turn into Wilds when eliminated; Wilds substitute for any symbol.',
      'Reaching 46,656 ways triggers Free Games with a rising multiplier and the one-shot Golden Treasure.',
      'Set your bet with ± and hit Spin (or Spacebar). Turbo speeds spins; Auto plays a chosen count.',
    ],
    component: lazy(() =>
      import('@playground/game-aether-dynasty/component').then((m) => ({ default: m.Board })),
    ),
  },
  {
    id: 'sky-crash',
    name: 'Sky Crash',
    description: 'A crash-style betting game — a jet takes off, the multiplier climbs, cash out before it flies away.',
    minPlayers: 1,
    maxPlayers: 1000,
    estimatedDurationMinutes: 5,
    tags: ['solo', 'classic', 'family'],
    howToPlay: [
      'Set a bet on one or both panels and press BET during the countdown.',
      'The jet takes off — the multiplier climbs from 1.00x and keeps accelerating.',
      'Press CASH OUT any time to bank your bet × the current multiplier.',
      'If the jet flies away before you cash out, the stake is gone.',
      'Use Auto cash out to bank automatically at a target multiplier.',
      'Every round’s crash point is derived from a blockchain block — provably fair.',
    ],
    component: lazy(() =>
      import('@playground/game-sky-crash/component').then((m) => ({ default: m.Board })),
    ),
  },
  {
    id: 'kana-mutti',
    name: 'Kana Mutti',
    description:
      "Sri Lanka's traditional Avurudu blindfold pot-smashing game — pick a clay pot, swing the pole and smash the lucky one to win.",
    minPlayers: 1,
    maxPlayers: 1000,
    estimatedDurationMinutes: 5,
    tags: ['solo', 'classic', 'family'],
    howToPlay: [
      'Everyone plays at the same global table — no room needed!',
      'Stake loyalty points and pick a risk level, then the striker is blindfolded.',
      'The riskier the level, the more empty pots — and the bigger each smash pays.',
      'Choose a pot to smash: hit a winning pot to bank your bet × the multiplier, or cash out early.',
      'Smash an empty pot and the round ends — the stake is gone.',
      'Quitting a round that is already dealt forfeits the stake and costs a 20-point early-leave fine.',
      'Every round is derived from a blockchain block — provably fair, revealed once the round ends.',
    ],
    component: lazy(() =>
      import('@playground/game-kana-mutti/component').then((m) => ({ default: m.Board })),
    ),
  },
  {
    id: 'kamba-adeema',
    name: 'Kamba Adeema',
    description:
      "Sri Lanka's Avurudu tug of war — four a side on one rope. Back a side for the next heave at 1.92×, and a four-all draw hands your stake straight back.",
    minPlayers: 1,
    maxPlayers: 1000,
    estimatedDurationMinutes: 5,
    tags: ['solo', 'classic', 'family'],
    howToPlay: [
      'Everyone plays at the same global table — no room needed!',
      'Take the rope: four pullers a side, Yati Pila on the left and Uda Pila on the right.',
      'One bet, and it is the whole board — who takes the NEXT heave. Tap a side to back it.',
      'Tap the backed side again to take the bet off, or the other side to move it across.',
      'Call HEAVE. Eight grips are tossed; the side that holds more of them takes the heave.',
      'Backing the winning side pays 1.92× your stake — four all is a draw and refunds it.',
      'The same swing then hauls the rope. First side to drag the ribbon over its line wins the match.',
      'Every heave is derived from one blockchain block — provably fair, revealed once the match ends.',
    ],
    component: lazy(() =>
      import('@playground/game-kamba-adeema/component').then((m) => ({ default: m.Board })),
    ),
  },
  {
    id: 'coin-duel',
    name: 'Coin Duel',
    description:
      'Two duelists, five coins, one bet. Back SUN or MOON to land on more of them — both faces are priced fresh every round, and because five is odd there is never a tie and every stake resolves.',
    minPlayers: 1,
    maxPlayers: 1000,
    estimatedDurationMinutes: 5,
    tags: ['solo', 'classic', 'family'],
    howToPlay: [
      'Everyone plays at the same global table — no room needed!',
      'Put a chip on SUN or MOON — whichever face you think lands on more of the five coins.',
      'Check the two prices first: each face is priced fresh every round, and you get 20 seconds to pick.',
      'Toss the coins. The face that lands on more of them takes the round — three out of five is enough.',
      'Your bet settles at whatever price was on the button when you backed it. Five coins cannot split evenly, so there is never a tie and never a refund.',
      'The score is also the damage: 3-2 costs the loser 1 life, 4-1 costs 3, and a 5-0 clean sweep costs 5.',
      'Every coin thrown chips a shield. Under a third, a 4-1 or 5-0 smashes through it and does double damage — first duelist to zero loses.',
      'Every round is derived from one blockchain block — provably fair, revealed once the duel ends.',
    ],
    component: lazy(() =>
      import('@playground/game-coin-duel/component').then((m) => ({ default: m.Board })),
    ),
  },
  {
    id: 'collapse-factor',
    name: 'Collapse Factor',
    description:
      'A claw takes one block out of the tower every turn and sets it on top. Ride the climb and cash out before it goes — or back the pull it finally comes down on. The collapse factor is published before every pull, so you always know exactly what you are about to risk.',
    minPlayers: 1,
    maxPlayers: 1000,
    estimatedDurationMinutes: 5,
    tags: ['solo', 'classic', 'family'],
    howToPlay: [
      'Everyone plays at the same global table — no room needed!',
      'Pick a chip, then back the climb, a tumble band, or both. You get 20 seconds before the claw starts.',
      'The gauge is the collapse factor: the chance the NEXT pull brings the tower down. It starts at 4% and climbs 3.5% every pull.',
      'THE CLIMB: every pull the tower survives lifts your multiplier, up to 484.85× at pull 16 — which about 1 tower in 500 reaches. Cash out at the end of any pull; if it comes down while you are still on, the stake is gone.',
      'Every pull opens the same eight-second window to RIDE ON or CASH OUT. Let it run out and the run carries on — set Auto cash out before the claw starts if you mean to leave at a particular rung.',
      'THE TUMBLE: a separate bet on which pull brings it down — 1–3 pays 4.62×, 4–6 pays 2.72×, 7–9 pays 3.50×, 10+ pays 6.11×. It settles when the tower falls, whatever you did with the climb.',
      'Leaving a live run cashes the climb out at the rung the tower is standing on, but forfeits a tumble stake — that bet has not been decided yet.',
      'Every rung and every band returns exactly 97%, truncated never rounded up, and the whole tower is sealed to a blockchain block before the claw moves — revealed once it comes down.',
    ],
    component: lazy(() =>
      import('@playground/game-collapse-factor/component').then((m) => ({ default: m.Board })),
    ),
  },
  {
    id: 'whack-a-mole',
    name: 'Whack-A-Mole',
    description:
      'Fifteen seconds, nine holes, three speeds. Moles pay a point, golden moles five, bombs halve your bounty and a skull ends the round. The last four seconds pay double — and the whole board, its price included, is sealed to a blockchain block before the first creature appears.',
    minPlayers: 1,
    maxPlayers: 1000,
    estimatedDurationMinutes: 5,
    tags: ['solo', 'classic', 'family'],
    howToPlay: [
      'Everyone plays at the same global table — no room needed!',
      'Pick a speed and a chip. You get 10 seconds; the board arms itself when the clock runs out.',
      'THE SPEEDS ARE PRICED APART and the bands do not overlap: a faster board always pays more for the same share of it cleared, and gives you less time to reach each creature. A perfectly cleared board returns 88–91% on Steady, 91.5–94% on Brisk and 94.5–97% on Frantic — a ceiling, not an average, because this is a game of skill.',
      'Whack for 15 seconds. A mole is 1 point, a golden mole 5. A bomb HALVES what you have collected, and a skull ends the round with nothing — leave both alone and they cost you nothing.',
      'The last 4 seconds are the Golden Rush: everything pays double.',
      'CASH OUT whenever you like, or let the buzzer do it. Leaving a live round keeps only 40% of what is on the meter — the rest is forfeit, and the card says so before you go.',
      'Tap, click, or use the keys: Q W E / A S D / Z X C, or 7 8 9 / 4 5 6 / 1 2 3, laid out like the nine holes.',
      'Every board — its exact rate, and the kind and hole of every creature in order — is drawn from one blockchain block before the first pop, and published in full when the round ends.',
    ],
    component: lazy(() =>
      import('@playground/game-whack-a-mole/component').then((m) => ({ default: m.Board })),
    ),
  },
  {
    id: 'pixel-poker',
    name: 'Pixel Perfect Poker',
    description:
      'A retro pixel-art 6-max Sit & Go poker tournament — practice against AI bots or create and join real-money multiplayer tables.',
    minPlayers: 1,
    maxPlayers: 1000,
    estimatedDurationMinutes: 15,
    tags: ['strategy', 'classic', 'family'],
    howToPlay: [
      'Practice mode deals you into a play-money table against five AI rivals — no real currency moves.',
      'Create or join a multiplayer table to play for real credits: your buy-in is staked when the cards come out.',
      'Each hand you get two private cards; five community cards are dealt across the flop, turn and river.',
      'On your turn: Fold to bow out, Check/Call to stay in, or Raise to apply pressure.',
      'Make the best five-card hand from your two cards plus the shared board.',
      'Blinds rise over time and there are no rebuys — outlast the table to win the pot.',
    ],
    component: lazy(() =>
      import('@playground/game-pixel-poker/component').then((m) => ({ default: m.Board })),
    ),
  },
  {
    id: 'memory-card',
    name: 'Memory Card',
    description: 'Flip cards and match pairs. Memorize the board — the player with the most matches wins!',
    minPlayers: 2,
    maxPlayers: 6,
    estimatedDurationMinutes: 8,
    tags: ['classic', 'family', 'kids'],
    howToPlay: [
      'All cards are briefly shown face-up at the start — memorize them!',
      'Players take turns flipping two cards face-up.',
      'If the two cards match, you keep the pair, score a point, and go again.',
      'If they don\'t match, both cards flip back face-down after a moment and the next player goes.',
      'The game ends when all pairs are found. Most pairs wins!',
    ],
    options: [
      {
        key: 'gridSize',
        type: 'select' as const,
        label: 'Grid size',
        choices: [
          { value: '4x4', label: '4×4 — 8 pairs (easy)' },
          { value: '4x5', label: '4×5 — 10 pairs (medium)' },
          { value: '4x6', label: '4×6 — 12 pairs (hard)' },
        ],
        default: '4x5',
      },
    ],
    component: lazy(() =>
      import('@playground/game-memory-card/component').then((m) => ({ default: m.Board })),
    ),
  },
];

// ---------------------------------------------------------------------------
// Runtime registry for user-created games (loaded from server at runtime)
// ---------------------------------------------------------------------------

const USER_GAME_COMPONENT: LazyGameComponent = lazy(() =>
  import('@/routes/UserGameBoard.js').then((m) => ({ default: m.UserGameBoard })),
);

interface UserGameMeta extends Omit<GameMeta, 'component' | 'howToPlay'> {
  howToPlay?: string[];
  hasCustomUI?: boolean;
}

const userGamesMap = new Map<string, UserGameMeta>();
const userGameComponentsMap = new Map<string, LazyGameComponent>();

/** Register a user-created game (loaded from server at runtime). */
export function registerUserGame(meta: UserGameMeta): void {
  userGamesMap.set(meta.id, meta);

  if (meta.hasCustomUI) {
    // Load the custom Board bundle from the server via a blob URL
    const component = lazy(async () => {
      const res = await apiFetch(`/api/ai-builder/game/${meta.id}/ui-bundle`);
      if (!res.ok) throw new Error(`Failed to load UI bundle for ${meta.id}`);
      const code = await res.text();
      const blob = new Blob([code], { type: 'application/javascript' });
      const url = URL.createObjectURL(blob);
      try {
        const mod = await import(/* @vite-ignore */ url) as { Board?: ComponentType<GameComponentProps> };
        if (!mod.Board) throw new Error(`Board export not found in UI bundle for ${meta.id}`);
        return { default: mod.Board };
      } finally {
        URL.revokeObjectURL(url);
      }
    });
    userGameComponentsMap.set(meta.id, component);
  }
}

/** Fetch metadata for a single user game by ID and register it (no-op if already known). */
export async function ensureUserGameRegistered(gameId: string): Promise<void> {
  if (GAMES.some((g) => g.id === gameId)) return; // built-in game
  if (userGamesMap.has(gameId)) return;            // already registered

  try {
    const res = await apiFetch(`/api/ai-builder/game/${gameId}/meta`);
    if (!res.ok) return;
    const data = await res.json() as { id: string; meta: { name?: string; description?: string; minPlayers?: number; maxPlayers?: number }; hasCustomUI?: boolean };
    registerUserGame({
      id: data.id,
      name: data.meta.name ?? data.id,
      description: data.meta.description ?? '',
      minPlayers: data.meta.minPlayers ?? 2,
      maxPlayers: data.meta.maxPlayers ?? 8,
      estimatedDurationMinutes: 10,
      tags: [],
      hasCustomUI: data.hasCustomUI ?? false,
    });
  } catch { /* silently ignore */ }
}

// ---------------------------------------------------------------------------
// Lookup helpers
// ---------------------------------------------------------------------------

/** Return all registered game metadata (without the component). */
export function listGames(): Omit<GameMeta, 'component'>[] {
  return GAMES.map(({ component: _component, ...meta }) => meta);
}

/** Return the lazy component for a game, or null if not found. */
export function getGameComponent(gameId: string): LazyGameComponent | null {
  const known = GAMES.find((g) => g.id === gameId);
  if (known) return known.component;
  // User game with custom Board bundle
  const customComponent = userGameComponentsMap.get(gameId);
  if (customComponent) return customComponent;
  // User game → generic board
  if (userGamesMap.has(gameId)) return USER_GAME_COMPONENT;
  return null;
}

/** Return full metadata for a game, or null if not found. */
export function getGameMeta(gameId: string): Omit<GameMeta, 'component'> | null {
  const game = GAMES.find((g) => g.id === gameId);
  if (game) {
    const { component: _component, ...meta } = game;
    return meta;
  }
  const userGame = userGamesMap.get(gameId);
  if (userGame) {
    return { howToPlay: [], ...userGame };
  }
  return null;
}
